import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import moment from "moment";
import excelJs from "exceljs";
import UserListToolbar from "./UserListToolbar";
import UserListHead from "./UserListHead";
import SearchNotFound from "../../../SearchNotFound";
import ConfirmationOutputSubmissionModal from "../../../ConfirmationOutputSubmissionModal";
import Label from "../../../Label";
import Page from "../../../Page";
import Comment from "../../../profile/Comment";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import {
  getOutputSubmission,
  confirmOutputSubmissionAdmin,
} from "../../../../core/service/outputSubmission.service";

import {
  Card,
  Container,
  Grid,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  Link,
} from "@mui/material";
import { config } from "../../../../core/service/config.service";

const TABLE_HEAD = [
  { id: "id", label: "ID", alignRight: false },
  { id: "name", label: "Name", alignRight: false },
  { id: "status", label: "Status", alignRight: false },
  { id: "date", label: "Date", alignRight: false },
  { id: "mentor", label: "Mentor", alignRight: false },
  { id: "year", label: "Year", alignRight: false },
  { id: "program", label: "Program", alignRight: false },
];

const ROLE_ADMIN = "admin";
const ROLE_MENTOR = "mentor";
const ROLE_PHD3Y = "phd_3y";
const ROLE_PHD5Y = "phd_5y";
const ROLE_POSTDOC = "post_doc";

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const applySortFilter = (array, comparator, query) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return array.filter((e) => {
      const { title_en, name_en, middle_en, surname_en } = e;
      const name = `${title_en} ${name_en} ${middle_en || ""} ${surname_en}`;
      return name.toLowerCase().indexOf(query.toLowerCase()) !== -1;
    });
  }
  return stabilizedThis.map((el) => el[0]);
};

ConfirmationOutputSubmissionAdminTable.propTypes = {
  listOutput: PropTypes.array,
};

export default function ConfirmationOutputSubmissionAdminTable({ listOutput }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openDetailPhd, setOpenDetailPhd] = useState(false);
  const [moreDetailListOutputPhd, setMoreDetailListOutputPhd] = useState({});

  const openDetail = async (row) => {
    const { id } = row;
    const moreDetail = await getOutputSubmission(id);
    setMoreDetailListOutputPhd(moreDetail);
    setOpenDetailPhd(true);
  };

  const closeMoreDetail = () => {
    setMoreDetailListOutputPhd({});
    setOpenDetailPhd(false);
  };

  const adminConfirm = async (row, confirm) => {
    const { id } = row;
    await confirmOutputSubmissionAdmin(id, confirm);
    closeMoreDetail();
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - listOutput.length) : 0;

  const filteredUsers = applySortFilter(
    listOutput,
    getComparator(order, orderBy),
    filterName
  );

  const isUserNotFound = filteredUsers.length === 0;

  const exportExcel = async () => {
    const workbook = new excelJs.Workbook();
    const ws = workbook.addWorksheet(`Report`);
    ws.columns = [
      {
        header: "ID",
        key: "id",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Name",
        key: "name",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Status",
        key: "status",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Date",
        key: "date",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Mentor",
        key: "mentor",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Year",
        key: "year",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Program",
        key: "program",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
    ];

    ws.getRow(1).height = 30;

    await Promise.all(
      filteredUsers.map(async (val, index) => {
        ws.addRow([
          val.student_id,
          `${val.name_en} ${val.middle_en || ""} ${val.surname_en}`,
          val.mentor_approve === 0 && val.admin_approve === 0
            ? "Pending"
            : val.mentor_approve === 1
            ? "Approve"
            : val.admin_approve === 1
            ? "Approve"
            : "Disapprove",
          val.created_date,
          `${val.mentor_title_en} ${val.mentor_name_en} ${
            val.mentor_middle_en || ""
          } ${val.mentor_surname_en}`,
          val.role_nm === ROLE_POSTDOC
            ? "Post Doc"
            : val.role_nm === ROLE_PHD3Y
            ? "PhD 3 year"
            : "PhD 5 year",
          val.role_nm === ROLE_POSTDOC
            ? "Post Doc"
            : val.role_nm === ROLE_PHD3Y
            ? "PhD 3 year"
            : "PhD 5 year",
        ]);
        ws.getRow(index + 2).font = {
          ...ws.getRow(index + 2).font,
          bold: false,
        };
      })
    );

    workbook.xlsx
      .writeBuffer({
        base64: true,
      })
      .then((xls64) => {
        // build anchor tag and attach file (works in chrome)
        var a = document.createElement("a");
        var data = new Blob([xls64], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        var url = URL.createObjectURL(data);
        a.href = url;
        a.download = `OUTPUT_SUBMISSION_${moment().format(
          "YYYYMMDD_HHmmss"
        )}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 0);
      })
      .catch(function (error) {
        console.log(error.message);
      });
  };

  return (
    <Page title="Output Submission Admin">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Confirmation for Output Submission
          </Typography>
          <Link href={`${config.host_api}export/output-submission`}>
            <Button variant="contained">Export excel</Button>
          </Link>
        </Stack>

        <Card>
          <UserListToolbar
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={listOutput.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const {
                      id,
                      student_id,
                      name_en,
                      middle_en,
                      surname_en,
                      mentor_approve,
                      mentor_middle_en,
                      mentor_name_en,
                      mentor_surname_en,
                      mentor_title_en,
                      role_nm,
                      admin_approve,
                      created_date,
                    } = row;
                    const { publication_evidence } = moreDetailListOutputPhd;

                    return (
                      <TableRow tabIndex={-1} hover key={id}>
                        <TableCell />

                        <TableCell align="left">{student_id}</TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography>
                              {`${name_en} ${middle_en} ${surname_en}` || ""}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">
                          <Label
                            variant="ghost"
                            color={
                              mentor_approve === 0 && admin_approve === 0
                                ? "warning"
                                : mentor_approve === 1
                                ? "success"
                                : admin_approve === 1
                                ? "success"
                                : "error"
                            }
                          >
                            {mentor_approve === 0 && admin_approve === 0
                              ? "Pending"
                              : mentor_approve === 1
                              ? "Approve"
                              : admin_approve === 1
                              ? "Approve"
                              : "Disapprove"}
                          </Label>
                        </TableCell>

                        <TableCell
                          sx={{ textTransform: "capitalize" }}
                          align="left"
                        >
                          {created_date}
                        </TableCell>

                        <TableCell align="left">
                          {`${mentor_title_en} ${mentor_name_en} ${
                            mentor_middle_en || ""
                          } ${mentor_surname_en}` || ""}
                        </TableCell>

                        <TableCell align="left">
                          {role_nm === ROLE_POSTDOC
                            ? "Post Doc"
                            : role_nm === ROLE_PHD3Y
                            ? "PhD 3 year"
                            : "PhD 5 year"}
                        </TableCell>

                        <TableCell align="left">
                          {role_nm === ROLE_POSTDOC
                            ? "Post Doc"
                            : role_nm === ROLE_PHD3Y
                            ? "PhD 3 year"
                            : "PhD 5 year"}
                        </TableCell>

                        <TableCell align="left">
                          <IconButton onClick={() => openDetail(row)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>

                        <Dialog
                          open={openDetailPhd}
                          onClose={closeMoreDetail}
                          fullWidth={true}
                          maxWidth={"lg"}
                        >
                          <Stack alignItems="center">
                            <DialogTitle>Output Submission (PhD)</DialogTitle>
                          </Stack>
                          <DialogContent>
                            <Grid container spacing={2}>
                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Name:
                                      </Typography>
                                      <Typography>{`${moreDetailListOutputPhd.name_en} ${moreDetailListOutputPhd.middle_en} ${moreDetailListOutputPhd.surname_en}`}</Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Submit Date:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListOutputPhd.created_date}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>

                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Program:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListOutputPhd.role_nm}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Email:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListOutputPhd.email}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>
                            </Grid>

                            <Stack
                              direction="row"
                              spacing={3}
                              sx={{ paddingTop: 2 }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                Publication submitted:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>?</Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                Research Field:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {moreDetailListOutputPhd.yourResearchField}
                              </Typography>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={3}
                              sx={{ paddingTop: 2 }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                Journal ranking:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {publication_evidence
                                  ? publication_evidence[0].journalRanking
                                  : "-"}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                Publication Status:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {publication_evidence
                                  ? publication_evidence[0].publicationStatus
                                  : "-"}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                Type of research publication:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {publication_evidence
                                  ? publication_evidence[0]
                                      .typeResearchPublication
                                  : "-"}
                              </Typography>
                            </Stack>

                            <Stack
                              direction="row"
                              justifyContent="flex-start"
                              alignItems="flex-start"
                              spacing={12}
                              variant="subtitle2"
                              sx={{ paddingTop: 1 }}
                            >
                              <Typography variant="subtitle1">
                                Accepted Date:
                              </Typography>
                              <Typography>
                                {publication_evidence
                                  ? publication_evidence[0].acceptedDate
                                  : "-"}
                              </Typography>
                              <Typography variant="subtitle1">
                                Published Date:
                              </Typography>
                              <Typography>
                                {publication_evidence
                                  ? publication_evidence[0].publishedDate
                                  : "-"}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                DOI:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {publication_evidence
                                  ? publication_evidence[0].doi
                                  : "-"}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                File research publication:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>-</Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                Article Title:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {publication_evidence
                                  ? publication_evidence[0].articleTitle
                                  : "-"}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                Journal's Name:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {publication_evidence
                                  ? publication_evidence[0].journalName
                                  : "-"}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                Subject Area and Category:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {publication_evidence
                                  ? publication_evidence[0].subjectAreaCategory
                                  : "-"}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                Journal Ranking Percentage or Percentile:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {publication_evidence
                                  ? publication_evidence[0]
                                      .journalRankingPercentagePercentile
                                  : "-"}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                File Journal Ranking Evidence:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>-</Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                First Author:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {publication_evidence
                                  ? publication_evidence[0].firstAuthor
                                  : "-"}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                Corresponding Author:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {publication_evidence
                                  ? publication_evidence[0].correspondingAuthor
                                  : "-"}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={3}>
                              <Typography
                                variant="subtitle1"
                                sx={{ paddingTop: 1 }}
                              >
                                Co-Author:
                              </Typography>
                              <Typography sx={{ paddingTop: 1 }}>
                                {publication_evidence
                                  ? publication_evidence[0]
                                      .collaboratorFromTop100
                                  : "-"}
                              </Typography>
                            </Stack>

                            <ConfirmationOutputSubmissionModal
                              detail={publication_evidence || []}
                            />

                            <Comment user_id={id} sx={{ marginTop: 6 }} />
                          </DialogContent>
                          {moreDetailListOutputPhd.mentor_approve === 0
                            ? moreDetailListOutputPhd.admin_approve === 0 && (
                                <DialogActions>
                                  <Button
                                    onClick={() =>
                                      adminConfirm(moreDetailListOutputPhd, 2)
                                    }
                                  >
                                    Disapprove
                                  </Button>
                                  <Button
                                    onClick={() =>
                                      adminConfirm(moreDetailListOutputPhd, 1)
                                    }
                                  >
                                    Approve
                                  </Button>
                                </DialogActions>
                              )
                            : moreDetailListOutputPhd.admin_approve === 0 && (
                                <DialogActions>
                                  <Button
                                    onClick={() =>
                                      adminConfirm(moreDetailListOutputPhd, 3)
                                    }
                                  >
                                    Confirm
                                  </Button>
                                </DialogActions>
                              )}
                        </Dialog>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={listOutput.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
